import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import "react-day-picker/dist/style.css";
import { useIsMobile } from "@/hooks/useMediaQuery";

/**
 * Shared date/time fields:
 *  - AirBnB-style range picker (label adapts for accommodation check-in/out)
 *  - Optional start/end time inputs
 *  - Optional "Actual Arrival Time" for accommodations
 *
 * Flights in AddSegmentDialog hide the range picker (date is captured in the
 * blue Flight Lookup card instead) — pass `hideDateRange` to skip it here.
 *
 * `nightsLabel` is rendered under the picker for accommodations whose date
 * range is set; the parent computes nights so the helper stays stateless.
 */
export function SegmentDateTimeFields({
  segment,
  onChange,
  dateRange,
  onDateRangeSelect,
  updateStartDateTime,
  updateEndDateTime,
  hideDateRange = false,
  nightsLabel = null,
  testIdPrefix = "segment",
}) {
  const isMobile = useIsMobile();
  const type = segment.type;
  const isSingleLocation = ["accommodation", "activity"].includes(type);

  return (
    <>
      {!hideDateRange && (
        <div className="grid gap-3">
          <Label>
            {isSingleLocation
              ? type === "accommodation"
                ? "Check-in & Check-out Dates *"
                : "When *"
              : "When *"}
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="justify-start text-left font-normal h-auto py-2"
                data-testid={`${testIdPrefix}-date-range-picker`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "MMM dd, yyyy")} → {format(dateRange.to, "MMM dd, yyyy")}
                    </>
                  ) : (
                    format(dateRange.from, "MMM dd, yyyy")
                  )
                ) : (
                  <span className="text-muted-foreground">Select dates</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={onDateRangeSelect}
                numberOfMonths={isMobile ? 1 : 2}
                className="p-3"
              />
            </PopoverContent>
          </Popover>
          {nightsLabel}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="start-time" className="text-sm">Start Time (Optional)</Label>
          <Input
            id="start-time"
            data-testid={`${testIdPrefix}-start-time-input`}
            type="time"
            value={segment.start_time || ""}
            onChange={(e) => updateStartDateTime(segment.start_date, e.target.value)}
            placeholder="HH:MM"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="end-time" className="text-sm">End Time (Optional)</Label>
          <Input
            id="end-time"
            data-testid={`${testIdPrefix}-end-time-input`}
            type="time"
            value={segment.end_time || ""}
            onChange={(e) => updateEndDateTime(segment.end_date, e.target.value)}
            placeholder="HH:MM"
          />
        </div>
      </div>

      {type === "accommodation" && (
        <div className="grid gap-2">
          <Label htmlFor="actual_arrival">Actual Arrival Time</Label>
          <Input
            id="actual_arrival"
            data-testid={`${testIdPrefix}-actual-arrival-input`}
            type="time"
            value={segment.actual_arrival || ""}
            onChange={(e) => onChange({ actual_arrival: e.target.value })}
            placeholder="e.g., 20:30"
          />
          <p className="text-xs text-gray-500">Your actual arrival time (can be different from check-in time)</p>
        </div>
      )}
    </>
  );
}

export default SegmentDateTimeFields;
